/**
 * PurchaseOrders.jsx - Purchase Order Management
 * 
 * TODO: REFACTORING NEEDED (Code Quality Review)
 * This file contains multiple large components (1,900+ lines total):
 * - PurchaseOrderDetail (750+ lines) - Should split into:
 *   - OrderItemsList: Line items management
 *   - OrderHeader: PO header info and status
 *   - OrderActions: Action buttons (print, receive, cancel)
 *   - OrderStatusManager: Status transitions and workflow
 * - Main PurchaseOrders component - Should split into:
 *   - POList: List of purchase orders
 *   - POFilters: Status, date, supplier filters
 *   - CreatePODialog: New PO creation form
 * 
 * For now, the code is organized into logical sections with comments.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Search, ArrowLeft, Printer, Building2, Calendar, FileText, IndianRupee,
  AlertOctagon, CheckCircle2, PackageCheck, Banknote, Undo2, Pencil, Save,
  XCircle, RotateCcw, ClipboardCheck, Barcode,
} from "lucide-react";
import BarcodePrintDialog from "@/components/barcode/BarcodePrintDialog";

function taxLabel(t) {
  if (!t || t === "none") return "No bill";
  if (t === "gst") return "GST bill";
  if (t === "local") return "Local purchase";
  return t.toUpperCase();
}

function statusPill(s) {
  if (s === "paid") return "pill-normal";
  if (s === "partially_paid") return "pill-high";
  if (s === "refund_pending") return "pill-high";
  return "pill-urgent";
}

function statusLabel(s) {
  if (s === "partially_paid") return "partially paid";
  if (s === "refund_pending") return "refund pending";
  return s || "—";
}

function receivedPill(rs) {
  if (rs === "received") return "pill-normal";
  if (rs === "partial") return "pill-high";
  return "pill-low";
}

const DISPUTE_REASONS = [
  { value: "item", label: "Wrong item" },
  { value: "mrp", label: "MRP mismatch" },
  { value: "quantity", label: "Quantity short" },
  { value: "other", label: "Other" },
];

const MODE_CONFIG = {
  pending: {
    path: "/purchase",
    kicker: "Pending receipt",
    title: "Purchase",
    subtitle: "Orders placed with suppliers — waiting to be received. Click a purchase to see its items.",
    received_status: "pending,partial",
    empty: "No pending purchases. Raise one from the Requirement page.",
  },
  received: {
    path: "/orders",
    kicker: "Received",
    title: "Orders",
    subtitle: "Purchases that have been delivered and received. Click an order to see its items.",
    received_status: "received",
    empty: "No received orders yet. Once a purchase arrives and you mark it received, it shows up here.",
  },
};

export default function PurchaseOrders({ mode = "received" }) {
  const cfg = MODE_CONFIG[mode] || MODE_CONFIG.received;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [disputeFilter, setDisputeFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [searchMode, setSearchMode] = useState("po"); // "po" | "item"
  const [itemQ, setItemQ] = useState("");
  const [itemResults, setItemResults] = useState(null); // {items, total, query}
  const [itemSearching, setItemSearching] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // URL-driven detail selection — clicking sidebar link clears ?po= and returns to list
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const po = params.get("po");
    setSelectedId(po || null);
  }, [location.search]);

  const fetchPOs = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/purchase-orders", {
        params: { received_status: cfg.received_status },
      });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPOs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Item-mode search debounced
  useEffect(() => {
    if (searchMode !== "item") {
      setItemResults(null);
      return;
    }
    const needle = itemQ.trim();
    if (needle.length < 2) {
      setItemResults(null);
      return;
    }
    const t = setTimeout(async () => {
      setItemSearching(true);
      try {
        const { data } = await api.get("/purchase-orders/search-by-item", {
          params: { q: needle, received_status: cfg.received_status, limit: 100 },
        });
        setItemResults(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setItemSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [searchMode, itemQ, cfg.received_status]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return items.filter((po) => {
      if (statusFilter !== "all" && po.payment_status !== statusFilter) return false;
      if (disputeFilter === "disputed" && !po.dispute) return false;
      if (disputeFilter === "clear" && po.dispute) return false;
      if (!qq) return true;
      return (
        (po.supplier_name || "").toLowerCase().includes(qq) ||
        (po.invoice_number || "").toLowerCase().includes(qq) ||
        (po.id || "").toLowerCase().includes(qq)
      );
    });
  }, [items, q, statusFilter, disputeFilter]);

  const markPurchased = async (poId, entered) => {
    try {
      await api.post(`/purchase-orders/${poId}/mark-purchased`, { entered });
      toast.success(entered ? "Marked purchased — billing entry done" : "Undone — back in queue");
      // Refresh list and sidebar counts
      fetchPOs();
      window.dispatchEvent(new Event("dyynit:sidebar-refresh"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (selectedId) {
    return (
      <PurchaseOrderDetail
        poId={selectedId}
        mode={mode}
        backPath={cfg.path}
        backLabel={`Back to ${cfg.title.toLowerCase()}`}
        onBack={() => {
          setSelectedId(null);
          navigate(cfg.path, { replace: true });
          fetchPOs();
        }}
      />
    );
  }

  return (
    <div className="fade-up" data-testid={`${mode}-page`}>
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{cfg.kicker}</p>
        <h1 className="font-heading text-3xl tracking-tight">{cfg.title}</h1>
        <p className="text-sm text-muted-foreground mt-1">{cfg.subtitle}</p>
      </div>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-4">
        <div className="inline-flex rounded-md border border-[hsl(var(--border))] bg-white p-0.5 h-11 self-start">
          <button
            type="button"
            onClick={() => setSearchMode("po")}
            className={`px-3 rounded-[5px] text-sm font-medium transition-colors ${
              searchMode === "po" ? "bg-foreground text-white" : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="po-search-mode-po"
          >
            By PO
          </button>
          <button
            type="button"
            onClick={() => setSearchMode("item")}
            className={`px-3 rounded-[5px] text-sm font-medium transition-colors ${
              searchMode === "item" ? "bg-foreground text-white" : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="po-search-mode-item"
          >
            Find item
          </button>
        </div>

        {searchMode === "po" ? (
          <>
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by supplier, invoice # or PO id…"
                className="pl-9 h-11 bg-white"
                data-testid="po-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px] bg-white" data-testid="po-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All payments</SelectItem>
                <SelectItem value="unpaid">Unpaid</SelectItem>
                <SelectItem value="partially_paid">Partially paid</SelectItem>
                <SelectItem value="refund_pending">Refund pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
            <Select value={disputeFilter} onValueChange={setDisputeFilter}>
              <SelectTrigger className="w-[160px] bg-white" data-testid="po-dispute-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All orders</SelectItem>
                <SelectItem value="disputed">Disputed only</SelectItem>
                <SelectItem value="clear">Not disputed</SelectItem>
              </SelectContent>
            </Select>
          </>
        ) : (
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={itemQ}
              onChange={(e) => setItemQ(e.target.value)}
              placeholder="Search by manual barcode, auto barcode, product, group or brand…"
              className="pl-9 h-11 bg-white"
              data-testid="po-item-search-input"
              autoFocus
            />
          </div>
        )}
      </div>

      {searchMode === "item" ? (
        <ItemSearchResults
          loading={itemSearching}
          results={itemResults}
          itemQ={itemQ}
          cfg={cfg}
          navigate={navigate}
        />
      ) : (
      <Card className="overflow-hidden" data-testid="po-table-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--muted))]">
              <tr className="text-left">
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">PO</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Supplier</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Invoice #</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Date</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Items</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Total</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Payment</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Received</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Flag</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Due</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((po) => (
                <tr
                  key={po.id}
                  className="data-row border-t border-[hsl(var(--border))] cursor-pointer transition-colors"
                  onClick={() => navigate(`${cfg.path}?po=${po.id}`)}
                  data-testid={`po-row-${po.id}`}
                >
                  <td className="p-3 font-mono-data text-xs text-muted-foreground" title={po.id}>
                    {po.id.slice(0, 8)}
                  </td>
                  <td className="p-3 font-medium">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-3.5 h-3.5 text-[#9E6D5B]" />
                      {po.supplier_name || "—"}
                    </div>
                  </td>
                  <td className="p-3 font-mono-data text-xs">{po.invoice_number || "—"}</td>
                  <td className="p-3 text-xs font-mono-data text-muted-foreground">{formatDate(po.created_at)}</td>
                  <td className="p-3 text-right font-mono-data">{po.line_items?.length || 0}</td>
                  <td className="p-3 text-right font-mono-data font-semibold">₹{Number(po.total || 0).toFixed(2)}</td>
                  <td className="p-3">
                    <span className={`pill ${statusPill(po.payment_status)}`}>
                      {statusLabel(po.payment_status)}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className={`pill ${receivedPill(po.received_status)}`}>
                      {po.received_status || "pending"}
                    </span>
                    {po.received_status === "received" && (
                      <div className="mt-1">
                        {po.purchase_entry ? (
                          <span
                            className="pill pill-normal"
                            data-testid={`po-purchased-pill-${po.id}`}
                            title={
                              po.purchase_entered_at
                                ? `Billing entry done · ${formatDateTime(po.purchase_entered_at)}${
                                    po.purchase_entered_by_name ? " · " + po.purchase_entered_by_name : ""
                                  }`
                                : "Billing entry done"
                            }
                          >
                            <ClipboardCheck className="w-3 h-3" /> purchased
                          </span>
                        ) : (
                          <span
                            className="pill pill-high"
                            data-testid={`po-purchase-pending-pill-${po.id}`}
                            title="Pending billing entry — mark purchased once entered into billing software"
                          >
                            billing entry pending
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    {po.dispute ? (
                      <span
                        className="pill pill-urgent"
                        data-testid={`po-dispute-badge-${po.id}`}
                        title={po.dispute_note || ""}
                      >
                        <AlertOctagon className="w-3 h-3" />
                        {po.dispute_reason || "dispute"}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="p-3 text-xs font-mono-data text-muted-foreground">{formatDate(po.due_date)}</td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      {mode === "received" && po.received_status === "received" && !po.purchase_entry && (
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            markPurchased(po.id, true);
                          }}
                          style={{ backgroundColor: "#9E6D5B" }}
                          data-testid={`po-mark-purchased-btn-${po.id}`}
                          title="Mark this PO as entered into the external billing software"
                        >
                          <ClipboardCheck className="w-3.5 h-3.5 mr-1" /> Mark purchased
                        </Button>
                      )}
                      {mode === "received" && po.received_status === "received" && po.purchase_entry && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            markPurchased(po.id, false);
                          }}
                          data-testid={`po-undo-purchased-btn-${po.id}`}
                          title="Undo billing entry mark (re-queues this order for entry)"
                        >
                          <Undo2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`/orders/${po.id}/print`, "_blank", "noopener");
                        }}
                        data-testid={`po-print-btn-${po.id}`}
                      >
                        <Printer className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {!filtered.length && !loading && (
                <tr>
                  <td colSpan={11} className="p-10 text-center text-sm text-muted-foreground">
                    {items.length === 0
                      ? cfg.empty
                      : "No orders match your filters."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      )}
    </div>
  );
}

function ItemSearchResults({ loading, results, itemQ, cfg, navigate }) {
  if (itemQ.trim().length < 2) {
    return (
      <Card className="p-10 text-center text-sm text-muted-foreground" data-testid="item-search-empty">
        Type at least 2 characters to search items across all {cfg.title.toLowerCase()}.
      </Card>
    );
  }
  if (loading) {
    return (
      <Card className="p-10 text-center text-sm text-muted-foreground" data-testid="item-search-loading">
        Searching…
      </Card>
    );
  }
  const pos = results?.items || [];
  if (!pos.length) {
    return (
      <Card className="p-10 text-center text-sm text-muted-foreground" data-testid="item-search-no-results">
        No items match <strong>{itemQ}</strong> in {cfg.title.toLowerCase()}.
      </Card>
    );
  }
  return (
    <Card className="overflow-hidden" data-testid="item-search-results-card">
      <div className="p-3 border-b border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 text-xs text-muted-foreground">
        <strong className="text-foreground">{pos.length}</strong> {cfg.title.toLowerCase()} contain items matching
        <span className="mx-1 font-mono-data text-foreground">"{itemQ}"</span>
        · newest first.
      </div>
      <div className="divide-y divide-[hsl(var(--border))]">
        {pos.map((po) => {
          const matched = (po.matched_line_indices || []).map((i) => po.line_items?.[i]).filter(Boolean);
          return (
            <button
              key={po.id}
              onClick={() => navigate(`${cfg.path}?po=${po.id}`)}
              className="w-full text-left p-4 hover:bg-[hsl(var(--muted))] transition-colors"
              data-testid={`item-search-po-${po.id}`}
            >
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span className="font-mono-data text-xs text-muted-foreground">{po.id.slice(0, 8)}</span>
                <Building2 className="w-3.5 h-3.5 text-[#9E6D5B]" />
                <span className="font-medium">{po.supplier_name || "—"}</span>
                <span className="text-xs text-muted-foreground font-mono-data">{formatDate(po.created_at)}</span>
                <span className={`pill ${statusPill(po.payment_status)} ml-auto`}>{statusLabel(po.payment_status)}</span>
                <span className={`pill ${receivedPill(po.received_status)}`}>{po.received_status || "pending"}</span>
              </div>
              <div className="space-y-1 pl-1">
                {matched.map((l) => (
                  <div key={l.id || `${l.product_snapshot?.product}-${l.quantity}-${l.rate}`} className="text-sm flex items-center gap-2">
                    <span className="inline-block w-1 h-1 rounded-full bg-[#9E6D5B]" />
                    <span className="font-medium truncate max-w-[360px]" title={l.product_snapshot?.product}>
                      {l.product_snapshot?.product}
                    </span>
                    <span className="text-xs text-muted-foreground font-mono-data">
                      · qty {l.quantity} · ₹{Number(l.rate || 0).toFixed(2)}
                    </span>
                    {l.canceled && <span className="pill pill-urgent">canceled</span>}
                    {l.received && !l.canceled && <span className="pill pill-normal">received</span>}
                  </div>
                ))}
              </div>
            </button>
          );
        })}
      </div>
    </Card>
  );
}

function PurchaseOrderDetail({ poId, onBack, backLabel = "Back to orders", mode = "received" }) {
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [codOpen, setCodOpen] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [cancelLine, setCancelLine] = useState(null); // {idx, mode: "cancel"|"reject"}
  const [refundEntry, setRefundEntry] = useState(null); // refund obj
  const [cancelPOOpen, setCancelPOOpen] = useState(false);
  const [pendingReceive, setPendingReceive] = useState(new Set()); // track locally selected items for CONFIRM
  const [confirmingReceive, setConfirmingReceive] = useState(false);
  // Barcode-print selection (independent from receive-selection so users can
  // pick a different subset of items to print labels for, regardless of
  // received status).
  const [barcodeSelected, setBarcodeSelected] = useState(new Set());
  const [barcodeDialogOpen, setBarcodeDialogOpen] = useState(false);

  // Memoized barcode-source product list — only recomputes when selection or line_items change.
  // Avoids the previous inline sort/map/filter on every render of PurchaseOrderDetail.
  const barcodeSourceProducts = useMemo(() => {
    return Array.from(barcodeSelected)
      .sort((a, b) => a - b)
      .map((idx) => {
        const line = po?.line_items?.[idx];
        if (!line) return null;
        const snap = line.product_snapshot || {};
        return {
          ...snap,
          product_id: line.product_id,
          quantity: line.quantity, // received quantity is the default
        };
      })
      .filter(Boolean);
  }, [barcodeSelected, po?.line_items]);

  const reload = async () => {
    try {
      const r = await api.get(`/purchase-orders/${poId}`);
      setPo(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    setLoading(true);
    api
      .get(`/purchase-orders/${poId}`)
      .then((r) => setPo(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [poId]);

  const markUnpaid = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/purchase-orders/${po.id}`, { payment_status: "unpaid" });
      setPo(data);
      toast.success("Marked as unpaid");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const patchPO = async (patch) => {
    const { data } = await api.patch(`/purchase-orders/${po.id}`, patch);
    setPo(data);
    return data;
  };

  const toggleLineReceived = async (idx, received) => {
    try {
      const { data } = await api.post(
        `/purchase-orders/${po.id}/lines/${idx}/receive`,
        { received },
      );
      setPo(data);
      window.dispatchEvent(new Event("dyynit:sidebar-refresh"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Toggle local selection for pending receive (used in "pending" mode)
  const togglePendingReceive = (idx) => {
    setPendingReceive((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  // Confirm receive - bulk mark selected items as received
  const confirmReceive = async () => {
    if (pendingReceive.size === 0) return;
    setConfirmingReceive(true);
    try {
      // Mark each selected item as received
      for (const idx of pendingReceive) {
        await api.post(`/purchase-orders/${po.id}/lines/${idx}/receive`, { received: true });
      }
      // Reload the PO to get updated state
      const { data } = await api.get(`/purchase-orders/${poId}`);
      setPo(data);
      setPendingReceive(new Set());
      toast.success(`${pendingReceive.size} item(s) marked as received`);
      window.dispatchEvent(new Event("dyynit:sidebar-refresh"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setConfirmingReceive(false);
    }
  };

  const detailMarkPurchased = async (entered) => {
    try {
      const { data } = await api.post(`/purchase-orders/${po.id}/mark-purchased`, { entered });
      setPo(data);
      toast.success(entered ? "Marked purchased — billing entry done" : "Undone — back in queue");
      window.dispatchEvent(new Event("dyynit:sidebar-refresh"));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resolveDispute = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/purchase-orders/${po.id}/resolve-dispute`);
      setPo(data);
      toast.success("Dispute resolved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !po) {
    return (
      <div className="text-sm text-muted-foreground" data-testid="po-detail-loading">Loading…</div>
    );
  }

  const isCOD = po.payment_mode === "cod";
  const isUnpaidish = po.payment_status !== "paid";

  return (
    <div className="fade-up" data-testid="po-detail-page">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="po-detail-back">
          <ArrowLeft className="w-4 h-4 mr-2" /> {backLabel}
        </Button>
        <div className="ml-auto flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => window.open(`/orders/${po.id}/print`, "_blank", "noopener")}
            data-testid="po-detail-print-btn"
          >
            <Printer className="w-4 h-4 mr-2" /> Print invoice
          </Button>

          {isCOD && isUnpaidish && (
            <Button
              onClick={() => setCodOpen(true)}
              style={{ backgroundColor: "#2D6A4F" }}
              data-testid="po-cod-paid-btn"
            >
              <Banknote className="w-4 h-4 mr-2" /> Record COD payment
            </Button>
          )}

          {isUnpaidish && (
            <Button
              onClick={() => setMarkPaidOpen(true)}
              disabled={saving}
              style={{ backgroundColor: "#9E6D5B" }}
              data-testid="po-mark-paid-btn"
            >
              Mark paid
            </Button>
          )}

          {po.payment_status === "paid" && (
            <Button
              variant="outline"
              onClick={markUnpaid}
              disabled={saving}
              data-testid="po-mark-unpaid-btn"
            >
              <Undo2 className="w-4 h-4 mr-2" /> Mark unpaid
            </Button>
          )}

          {po.dispute ? (
            <Button
              variant="outline"
              onClick={resolveDispute}
              disabled={saving}
              data-testid="po-resolve-dispute-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" /> Resolve dispute
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => setDisputeOpen(true)}
              className="border-red-300 text-red-700 hover:bg-red-50"
              data-testid="po-raise-dispute-btn"
            >
              <AlertOctagon className="w-4 h-4 mr-2" /> Raise dispute
            </Button>
          )}

          {po.status !== "canceled" && (
            <Button
              variant="outline"
              onClick={() => setCancelPOOpen(true)}
              className="border-red-300 text-red-700 hover:bg-red-50"
              data-testid="po-cancel-entire-btn"
            >
              <XCircle className="w-4 h-4 mr-2" /> Cancel entire PO
            </Button>
          )}
        </div>
      </div>

      {po.status === "canceled" && (
        <Card className="p-4 mb-4 border-red-300 bg-red-50" data-testid="po-canceled-banner">
          <div className="flex items-start gap-3">
            <XCircle className="w-5 h-5 text-red-700 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-medium text-red-900">
                Entire PO canceled
              </div>
              {po.cancel_note && (
                <div className="text-xs text-red-800 mt-1 whitespace-pre-wrap">{po.cancel_note}</div>
              )}
              <div className="text-xs text-muted-foreground mt-1">
                {po.canceled_at ? formatDateTime(po.canceled_at) : ""}
                {po.canceled_by_name ? ` · by ${po.canceled_by_name}` : ""}
              </div>
            </div>
          </div>
        </Card>
      )}

      {po.dispute && (
        <Card className="p-4 mb-4 border-red-300 bg-red-50" data-testid="po-dispute-banner">
          <div className="flex items-start gap-3">
            <AlertOctagon className="w-5 h-5 text-red-700 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-medium text-red-900">
                Disputed · {po.dispute_reason || "—"}
              </div>
              {po.dispute_note && (
                <div className="text-xs text-red-800 mt-1 whitespace-pre-wrap">{po.dispute_note}</div>
              )}
              <div className="text-xs text-muted-foreground mt-1">
                Raised {po.disputed_at ? formatDateTime(po.disputed_at) : ""}
                {po.disputed_by_name ? ` · by ${po.disputed_by_name}` : ""}
              </div>
            </div>
          </div>
        </Card>
      )}

      <div className="grid md:grid-cols-3 gap-4 mb-4">
        <Card className="p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Supplier</div>
          <div className="font-heading text-lg flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#9E6D5B]" /> {po.supplier_name}
          </div>
        </Card>
        <Card className="p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Created</div>
          <div className="text-sm font-mono-data flex items-center gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground" /> {formatDateTime(po.created_at)}
          </div>
          <div className="text-xs text-muted-foreground mt-1">by {po.created_by_name || "—"}</div>
        </Card>
        <Card className="p-5">
          <InlineEditField
            label="Invoice #"
            value={po.invoice_number}
            icon={<FileText className="w-4 h-4 text-muted-foreground" />}
            placeholder="Enter invoice #"
            onSave={(v) => patchPO({ invoice_number: v })}
            testid="po-edit-invoice"
          />
        </Card>
      </div>

      <div className="grid md:grid-cols-5 gap-4 mb-4">
        <Card className="p-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Payment</div>
          <span className={`pill ${statusPill(po.payment_status)} text-sm`} data-testid="po-detail-payment-pill">
            {statusLabel(po.payment_status)}
          </span>
          {Number(po.amount_paid || 0) > 0 && po.payment_status !== "unpaid" && (
            <div className="text-xs text-muted-foreground mt-1 font-mono-data">
              paid ₹{Number(po.amount_paid).toFixed(2)} / ₹{Number(po.total || 0).toFixed(2)}
            </div>
          )}
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Mode</div>
          <div className="font-medium uppercase text-sm">{po.payment_mode}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Received</div>
          <span className={`pill ${receivedPill(po.received_status)} text-sm`} data-testid="po-detail-received-pill">
            <PackageCheck className="w-3 h-3" /> {po.received_status || "pending"}
          </span>
          {/* Show Mark purchased section only in received mode, not in pending mode */}
          {mode === "received" && po.received_status === "received" && (
            <div className="mt-2">
              {po.purchase_entry ? (
                <div className="space-y-1.5">
                  <span
                    className="pill pill-normal text-xs"
                    data-testid="po-detail-purchased-pill"
                    title={
                      po.purchase_entered_at
                        ? `Billing entry done · ${formatDateTime(po.purchase_entered_at)}`
                        : "Billing entry done"
                    }
                  >
                    <ClipboardCheck className="w-3 h-3" /> billing entry done
                  </span>
                  {po.purchase_entered_by_name && (
                    <div className="text-[10px] text-muted-foreground font-mono-data">
                      by {po.purchase_entered_by_name}
                      {po.purchase_entered_at ? " · " + formatDateTime(po.purchase_entered_at) : ""}
                    </div>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => detailMarkPurchased(false)}
                    data-testid="po-detail-undo-purchased-btn"
                    className="w-full mt-1"
                  >
                    <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Undo
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  onClick={() => detailMarkPurchased(true)}
                  style={{ backgroundColor: "#9E6D5B" }}
                  data-testid="po-detail-mark-purchased-btn"
                  className="w-full mt-1"
                  title="Mark as entered into the external billing software"
                >
                  <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" /> Mark purchased
                </Button>
              )}
            </div>
          )}
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Bill type</div>
          <div className="font-medium text-sm">{taxLabel(po.tax_type)}</div>
        </Card>
        <Card className="p-4">
          <InlineEditField
            label="Due"
            type="date"
            value={po.due_date}
            display={formatDate(po.due_date)}
            onSave={(v) => patchPO({ due_date: v || null })}
            testid="po-edit-due"
          />
        </Card>
      </div>

      <Card className="overflow-hidden" data-testid="po-lines-card">
        {mode === "received" && (
          <div
            className="px-4 py-2 border-b border-[hsl(var(--border))] bg-muted/30 flex items-center gap-3 flex-wrap"
            data-testid="po-barcode-toolbar"
          >
            <span className="text-xs text-muted-foreground">
              <Barcode className="w-3 h-3 inline-block mr-1" />
              Select rows to print barcode labels (received qty auto-fills as default)
            </span>
            <span className="text-xs font-mono-data text-muted-foreground ml-auto">
              {barcodeSelected.size} selected
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                // Select all non-canceled lines that have an auto_barcode
                const next = new Set();
                (po.line_items || []).forEach((l, i) => {
                  if (!l.canceled && (l.product_snapshot?.auto_barcode)) next.add(i);
                });
                setBarcodeSelected(next);
              }}
              data-testid="po-barcode-select-all"
              className="h-7 text-xs"
            >
              Select all
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setBarcodeSelected(new Set())}
              data-testid="po-barcode-clear"
              className="h-7 text-xs"
            >
              Clear
            </Button>
            <Button
              size="sm"
              onClick={() => {
                if (barcodeSelected.size === 0) {
                  toast.error("Select at least one product first");
                  return;
                }
                setBarcodeDialogOpen(true);
              }}
              disabled={barcodeSelected.size === 0}
              data-testid="po-barcode-print-btn"
              className="h-7 text-xs"
              style={{ backgroundColor: "#2D6A4F" }}
            >
              <Barcode className="w-3.5 h-3.5 mr-1" /> Print Barcode
            </Button>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--muted))]">
              <tr className="text-left">
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium w-12">Got</th>
                {mode === "received" && (
                  <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium w-12" title="Select for barcode print">
                    <Barcode className="w-3 h-3 inline-block" />
                  </th>
                )}
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Product</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Brand</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">Barcode</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Qty</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Rate</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Amount</th>
                <th className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {(po.line_items || []).map((l, idx) => {
                const p = l.product_snapshot || {};
                const isCanceled = !!l.canceled;
                const isPending = mode === "pending";
                // In pending mode: use local selection state for unconfirmed items
                // In received mode: show actual received state
                const isLocallySelected = isPending && !l.received && pendingReceive.has(idx);
                const showAsChecked = l.received || isLocallySelected;
                
                return (
                  <tr
                    key={l.id || `line-${idx}`}
                    className={`border-t border-[hsl(var(--border))] ${
                      isCanceled ? "bg-red-50/40 opacity-70" : l.received ? "bg-emerald-50/50" : isLocallySelected ? "bg-amber-50/50" : ""
                    }`}
                    data-testid={`po-line-row-${idx}`}
                  >
                    <td className="p-3">
                      <Checkbox
                        checked={showAsChecked}
                        disabled={isCanceled || l.received}
                        onCheckedChange={(v) => {
                          if (isPending && !l.received) {
                            // In pending mode, toggle local selection
                            togglePendingReceive(idx);
                          } else {
                            // In received mode, directly toggle (for undo)
                            toggleLineReceived(idx, !!v);
                          }
                        }}
                        data-testid={`po-line-received-${idx}`}
                      />
                    </td>
                    {mode === "received" && (
                      <td className="p-3">
                        <Checkbox
                          checked={barcodeSelected.has(idx)}
                          disabled={isCanceled || !p.auto_barcode}
                          onCheckedChange={(v) => {
                            setBarcodeSelected((prev) => {
                              const next = new Set(prev);
                              if (v) next.add(idx);
                              else next.delete(idx);
                              return next;
                            });
                          }}
                          data-testid={`po-line-barcode-${idx}`}
                          title={
                            !p.auto_barcode
                              ? "No auto barcode — cannot print"
                              : "Include in barcode print"
                          }
                        />
                      </td>
                    )}
                    <td className="p-3 max-w-[320px]">
                      <div className={`font-medium truncate ${isCanceled ? "line-through" : ""}`} title={p.product}>
                        {p.product}
                      </div>
                      {isCanceled && (
                        <div className="text-[10px] text-red-700 font-mono-data mt-0.5" data-testid={`po-line-canceled-${idx}`}>
                          {l.canceled_reason === "reject" ? "REJECTED" : "CANCELED"}
                          {l.canceled_at ? ` · ${formatDateTime(l.canceled_at)}` : ""}
                          {l.canceled_by_name ? ` · ${l.canceled_by_name}` : ""}
                          {l.canceled_note ? ` · ${l.canceled_note}` : ""}
                        </div>
                      )}
                      {!isCanceled && l.received_at && (
                        <div className="text-[10px] text-emerald-700 font-mono-data mt-0.5">
                          received {formatDateTime(l.received_at)}
                          {l.received_by_name ? ` · ${l.received_by_name}` : ""}
                        </div>
                      )}
                      {!isCanceled && isLocallySelected && !l.received && (
                        <div className="text-[10px] text-amber-700 font-mono-data mt-0.5">
                          selected for confirmation
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-sm">{p.brand_name || "—"}</td>
                    <td className="p-3 text-xs font-mono-data text-muted-foreground">{p.auto_barcode}</td>
                    <td className="p-3 text-right font-mono-data">{l.quantity}</td>
                    <td className="p-3 text-right font-mono-data">₹{Number(l.rate || 0).toFixed(2)}</td>
                    <td className="p-3 text-right font-mono-data font-semibold">
                      {isCanceled ? (
                        <span className="text-muted-foreground line-through">₹{Number(l.amount || 0).toFixed(2)}</span>
                      ) : (
                        <>₹{Number(l.amount || 0).toFixed(2)}</>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      {!isCanceled && !l.received && !isLocallySelected && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setCancelLine({ idx, mode: "cancel" })}
                          className="text-red-700 hover:bg-red-50"
                          data-testid={`po-line-cancel-${idx}`}
                          title="Cancel this item — returns to Requirements"
                        >
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Cancel
                        </Button>
                      )}
                      {!isCanceled && l.received && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setCancelLine({ idx, mode: "reject" })}
                          className="text-red-700 hover:bg-red-50"
                          data-testid={`po-line-reject-${idx}`}
                          title="Reject received item — returns to Requirements"
                        >
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="border-t border-[hsl(var(--border))]">
              <tr>
                <td colSpan={mode === "received" ? 7 : 6} className="p-3 text-right text-sm">Subtotal</td>
                <td className="p-3 text-right font-mono-data">₹{Number(po.subtotal || 0).toFixed(2)}</td>
                <td />
              </tr>
              {po.tax_type !== "none" && (
                <tr>
                  <td colSpan={mode === "received" ? 7 : 6} className="p-3 text-right text-xs text-muted-foreground">
                    Bill type: {taxLabel(po.tax_type)}
                  </td>
                  <td className="p-3" />
                  <td />
                </tr>
              )}
              <tr className="bg-[hsl(var(--muted))]">
                <td colSpan={mode === "received" ? 7 : 6} className="p-3 text-right text-base font-semibold">Total</td>
                <td className="p-3 text-right font-mono-data text-base font-semibold text-[#9E6D5B]">
                  ₹{Number(po.total || 0).toFixed(2)}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
        
        {/* CONFIRM button for pending receive - only in pending mode */}
        {mode === "pending" && pendingReceive.size > 0 && (
          <div className="mt-4 flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="text-sm">
              <span className="font-semibold text-amber-800">{pendingReceive.size} item(s)</span>
              <span className="text-amber-700"> selected for receiving</span>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPendingReceive(new Set())}
                data-testid="po-clear-selection-btn"
              >
                Clear
              </Button>
              <Button
                size="sm"
                onClick={confirmReceive}
                disabled={confirmingReceive}
                style={{ backgroundColor: "#9E6D5B" }}
                data-testid="po-confirm-receive-btn"
              >
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                {confirmingReceive ? "Confirming…" : "Confirm received"}
              </Button>
            </div>
          </div>
        )}
      </Card>

      {(po.refunds || []).length > 0 && (
        <Card className="mt-4 p-5" data-testid="po-refunds-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Refunds</div>
              <div className="text-sm font-mono-data mt-1">
                Due <span className="font-semibold">₹{Number(po.refund_due || 0).toFixed(2)}</span>
                {" · "}
                Received <span className="font-semibold">₹{Number(po.refund_received || 0).toFixed(2)}</span>
                {" · "}
                Pending <span className="font-semibold text-[#B4602A]">
                  ₹{Math.max(0, Number(po.refund_due || 0) - Number(po.refund_received || 0)).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
          <div className="divide-y divide-[hsl(var(--border))]">
            {po.refunds.map((r) => (
              <div key={r.id} className="py-2 flex items-center gap-3 text-sm" data-testid={`po-refund-${r.id}`}>
                <div className="flex-1">
                  <div className="font-mono-data">
                    ₹{Number(r.amount || 0).toFixed(2)} · {r.reason}
                    {r.note ? ` · ${r.note}` : ""}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    raised {formatDateTime(r.created_at)}
                    {r.created_by_name ? ` · ${r.created_by_name}` : ""}
                  </div>
                </div>
                {r.received ? (
                  <span className="pill pill-normal">
                    <CheckCircle2 className="w-3 h-3" /> received ₹{Number(r.received_amount || 0).toFixed(2)}
                  </span>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setRefundEntry(r)}
                    data-testid={`po-refund-receive-${r.id}`}
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-1" /> Mark received
                  </Button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="mt-4 p-5" data-testid="po-notes-card">
        <InlineEditField
          label="Notes"
          value={po.notes}
          multiline
          placeholder="Add a note…"
          onSave={(v) => patchPO({ notes: v })}
          testid="po-edit-notes"
        />
      </Card>

      <CODPaidDialog
        open={codOpen}
        onOpenChange={setCodOpen}
        po={po}
        onDone={(updated) => setPo(updated)}
      />
      <DisputeDialog
        open={disputeOpen}
        onOpenChange={setDisputeOpen}
        po={po}
        onDone={(updated) => setPo(updated)}
      />
      <MarkPaidDialog
        open={markPaidOpen}
        onOpenChange={setMarkPaidOpen}
        po={po}
        onDone={(updated) => setPo(updated)}
      />
      <CancelLineDialog
        entry={cancelLine}
        po={po}
        onOpenChange={(v) => { if (!v) setCancelLine(null); }}
        onDone={(updated) => setPo(updated)}
      />
      <RefundReceivedDialog
        refund={refundEntry}
        po={po}
        onOpenChange={(v) => { if (!v) setRefundEntry(null); }}
        onDone={(updated) => setPo(updated)}
      />
      <CancelPODialog
        open={cancelPOOpen}
        po={po}
        onOpenChange={setCancelPOOpen}
        onDone={(updated) => setPo(updated)}
      />
      {/* Barcode print dialog — receives the snapshots of selected line items.
          Default quantity per row = the line's received `quantity`. */}
      <BarcodePrintDialog
        open={barcodeDialogOpen}
        onOpenChange={(v) => {
          setBarcodeDialogOpen(v);
          if (!v) {
            // Optional: keep selection so user can re-open if needed.
          }
        }}
        title={`Print Barcode — Order #${po.order_no || po.id?.slice(0, 6)}`}
        sourceProducts={barcodeSourceProducts}
        defaultQuantityKey="quantity"
      />
    </div>
  );
}

function CODPaidDialog({ open, onOpenChange, po, onDone }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [markReceived, setMarkReceived] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && po) {
      setAmount(String(po.total || ""));
      setNote("");
      setMarkReceived(true);
    }
  }, [open, po]);

  const total = Number(po?.total || 0);
  const amt = Number(amount || 0);
  const short = total - amt;

  const submit = async () => {
    if (amt <= 0) return toast.error("Enter a valid amount");
    setSaving(true);
    try {
      const { data } = await api.post(`/purchase-orders/${po.id}/cod-paid`, {
        amount_paid: amt,
        note: note || "",
        mark_received: markReceived,
      });
      toast.success(
        data.payment_status === "paid"
          ? "COD payment recorded"
          : `Partial COD recorded · ₹${short.toFixed(2)} short`,
      );
      onDone?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="cod-paid-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Record COD payment</DialogTitle>
          <DialogDescription>
            Enter the cash amount handed over. Short amounts will flag the PO as partially paid.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <div className="rounded-md bg-[hsl(var(--muted))] p-3 text-sm">
            <div className="flex justify-between">
              <span>PO total</span>
              <span className="font-mono-data font-semibold">₹{total.toFixed(2)}</span>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Amount paid ₹ *</Label>
            <Input
              type="number"
              step="0.01"
              min={0}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1 font-mono-data"
              data-testid="cod-amount-input"
              autoFocus
            />
            {amt > 0 && short > 0.005 && (
              <div className="text-xs text-[#B4602A] mt-1">
                Short by ₹{short.toFixed(2)} — will mark as <strong>partially paid</strong>.
              </div>
            )}
            {amt > 0 && short <= 0.005 && (
              <div className="text-xs text-emerald-700 mt-1">Settles the invoice in full.</div>
            )}
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Note (optional)</Label>
            <Textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="mt-1"
              data-testid="cod-note-input"
            />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <Checkbox
              checked={markReceived}
              onCheckedChange={(v) => setMarkReceived(!!v)}
              data-testid="cod-mark-received-check"
            />
            Also mark parcel received
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving}
            style={{ backgroundColor: "#2D6A4F" }}
            data-testid="cod-confirm-btn"
          >
            {saving ? "Saving…" : "Record payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DisputeDialog({ open, onOpenChange, po, onDone }) {
  const [reason, setReason] = useState("item");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setReason("item");
      setNote("");
    }
  }, [open]);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/purchase-orders/${po.id}/dispute`, {
        reason,
        note: note || "",
      });
      toast.success("Dispute raised");
      onDone?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="dispute-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Raise dispute</DialogTitle>
          <DialogDescription>
            Flag a discrepancy in the parcel — wrong item, MRP mismatch or short quantity.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Reason *</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="mt-1" data-testid="dispute-reason-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DISPUTE_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value} data-testid={`dispute-reason-${r.value}`}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Description</Label>
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. 3 units missing from shipment"
              className="mt-1"
              data-testid="dispute-note-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-red-700 hover:bg-red-800 text-white"
            data-testid="dispute-confirm-btn"
          >
            {saving ? "Saving…" : "Raise dispute"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MarkPaidDialog({ open, onOpenChange, po, onDone }) {
  const [mode, setMode] = useState("prepaid");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && po) {
      setMode(po.payment_mode || "prepaid");
      setInvoiceNumber(po.invoice_number || "");
    }
  }, [open, po]);

  const submit = async () => {
    setSaving(true);
    try {
      const patch = { payment_status: "paid", payment_mode: mode };
      if (invoiceNumber.trim() && invoiceNumber.trim() !== (po.invoice_number || "")) {
        patch.invoice_number = invoiceNumber.trim();
      }
      const { data } = await api.patch(`/purchase-orders/${po.id}`, patch);
      onDone?.(data);
      toast.success("Marked as paid");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="mark-paid-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Mark paid</DialogTitle>
          <DialogDescription>
            Settle this PO in full. Pick the mode you paid the supplier in.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md bg-[hsl(var(--muted))] p-3 text-sm flex justify-between">
            <span>PO total</span>
            <span className="font-mono-data font-semibold">₹{Number(po?.total || 0).toFixed(2)}</span>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Payment mode *</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="mt-1" data-testid="mark-paid-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="prepaid" data-testid="mark-paid-mode-prepaid">Prepaid (bank / UPI)</SelectItem>
                <SelectItem value="cod" data-testid="mark-paid-mode-cod">Cash on delivery</SelectItem>
                <SelectItem value="credit" data-testid="mark-paid-mode-credit">Credit (settled later)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Invoice # (optional)</Label>
            <Input
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              className="mt-1 font-mono-data"
              data-testid="mark-paid-invoice-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving}
            style={{ backgroundColor: "#9E6D5B" }}
            data-testid="mark-paid-confirm-btn"
          >
            {saving ? "Saving…" : "Confirm payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelLineDialog({ entry, po, onOpenChange, onDone }) {
  const [note, setNote] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const open = !!entry;
  const idx = entry?.idx ?? -1;
  const mode = entry?.mode || "cancel";
  const line = po?.line_items?.[idx] || null;
  const amountPaid = Number(po?.amount_paid || 0);
  const hasRefund = po && amountPaid > 0 && po.payment_status !== "unpaid";

  useEffect(() => {
    if (open && line) {
      setNote("");
      setRefundAmount(String(Number(line.amount || 0).toFixed(2)));
    }
  }, [open, line]);

  if (!open || !line) return null;

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        reason: mode,
        note: note.trim(),
      };
      if (hasRefund) {
        const amt = Number(refundAmount || 0);
        if (amt < 0) return toast.error("Refund amount cannot be negative");
        payload.refund_amount = amt;
      }
      const { data } = await api.post(
        `/purchase-orders/${po.id}/lines/${idx}/cancel`,
        payload,
      );
      onDone?.(data);
      toast.success(
        mode === "reject"
          ? "Item rejected — back to Requirements"
          : "Item canceled — back to Requirements",
      );
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const title = mode === "reject" ? "Reject this item" : "Cancel this item";
  const desc =
    mode === "reject"
      ? "The item will be removed from this order and a fresh requirement will be raised so you can reorder it."
      : "The item will be cancelled from this purchase and a fresh requirement will be raised so you can reorder it.";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="cancel-line-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">{title}</DialogTitle>
          <DialogDescription>{desc}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md bg-[hsl(var(--muted))] p-3 text-sm">
            <div className="font-medium truncate">{line.product_snapshot?.product}</div>
            <div className="text-xs text-muted-foreground font-mono-data mt-1">
              Qty {line.quantity} · ₹{Number(line.rate || 0).toFixed(2)} = ₹{Number(line.amount || 0).toFixed(2)}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Notes</Label>
            <Textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={mode === "reject" ? "e.g. Damaged on arrival" : "e.g. Supplier doesn't stock this"}
              className="mt-1"
              data-testid="cancel-line-note"
            />
          </div>
          {hasRefund && (
            <div className="rounded-md border border-[#B4602A]/40 bg-amber-50 p-3 space-y-2" data-testid="cancel-line-refund-block">
              <div className="text-xs text-[#B4602A] font-medium">
                This PO was already paid (₹{amountPaid.toFixed(2)}). Record a refund amount to track with the supplier.
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Refund amount ₹</Label>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  className="mt-1 font-mono-data"
                  data-testid="cancel-line-refund-amount"
                />
                <div className="text-[11px] text-muted-foreground mt-1">
                  Default is the line amount. Adjust if the supplier agreed a different number.
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Back</Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-red-700 hover:bg-red-800 text-white"
            data-testid="cancel-line-confirm-btn"
          >
            {saving ? "Saving…" : mode === "reject" ? "Reject & re-raise" : "Cancel & re-raise"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RefundReceivedDialog({ refund, po, onOpenChange, onDone }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const open = !!refund;

  useEffect(() => {
    if (open && refund) {
      setAmount(String(Number(refund.amount || 0).toFixed(2)));
      setNote("");
    }
  }, [open, refund]);

  if (!open) return null;

  const submit = async () => {
    const amt = Number(amount || 0);
    if (amt < 0) return toast.error("Amount cannot be negative");
    setSaving(true);
    try {
      const { data } = await api.post(
        `/purchase-orders/${po.id}/refunds/${refund.id}/received`,
        { amount: amt, note: note.trim() },
      );
      onDone?.(data);
      toast.success("Refund recorded");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="refund-received-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Mark refund received</DialogTitle>
          <DialogDescription>
            Confirm the amount the supplier refunded for the canceled/rejected item.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md bg-[hsl(var(--muted))] p-3 text-sm">
            <div className="flex justify-between">
              <span>Refund due</span>
              <span className="font-mono-data font-semibold">₹{Number(refund.amount || 0).toFixed(2)}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">{refund.reason} · {refund.note || "—"}</div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Amount received ₹</Label>
            <Input
              type="number"
              step="0.01"
              min={0}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1 font-mono-data"
              data-testid="refund-received-amount"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Note (optional)</Label>
            <Textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="mt-1"
              data-testid="refund-received-note"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving}
            style={{ backgroundColor: "#2D6A4F" }}
            data-testid="refund-received-confirm"
          >
            {saving ? "Saving…" : "Record refund"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function InlineEditField({ label, value, display, icon, placeholder, type, multiline, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  const commit = async () => {
    if (draft === (value || "")) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(draft);
      toast.success(`${label} updated`);
      setEditing(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="space-y-2" data-testid={`${testid}-editing`}>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
        {multiline ? (
          <Textarea
            autoFocus
            rows={3}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="mt-1"
            data-testid={`${testid}-input`}
          />
        ) : (
          <Input
            autoFocus
            type={type || "text"}
            value={draft || ""}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder}
            className="mt-1"
            data-testid={`${testid}-input`}
          />
        )}
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={commit}
            disabled={saving}
            style={{ backgroundColor: "#9E6D5B" }}
            data-testid={`${testid}-save`}
          >
            <Save className="w-3.5 h-3.5 mr-1" /> {saving ? "Saving…" : "Save"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setDraft(value || "");
              setEditing(false);
            }}
            data-testid={`${testid}-cancel`}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  const renderValue = () => {
    if (display) return display;
    if (!value) return <span className="text-muted-foreground italic">{placeholder || "—"}</span>;
    return value;
  };

  return (
    <div className="group" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-2">
        {label}
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
          data-testid={`${testid}-edit-btn`}
          title={`Edit ${label}`}
        >
          <Pencil className="w-3 h-3" />
        </button>
      </div>
      {multiline ? (
        <div
          className="text-sm whitespace-pre-wrap min-h-[1.25rem]"
          data-testid={`${testid}-value`}
        >
          {renderValue()}
        </div>
      ) : (
        <div
          className="font-mono-data text-base flex items-center gap-2"
          data-testid={`${testid}-value`}
        >
          {icon} <span className="truncate">{renderValue()}</span>
        </div>
      )}
    </div>
  );
}


function CancelPODialog({ open, po, onOpenChange, onDone }) {
  const [note, setNote] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const activeLines = (po?.line_items || []).filter((l) => !l.canceled);
  const activeValue = activeLines.reduce((a, l) => a + Number(l.amount || 0), 0);
  const amountPaid = Number(po?.amount_paid || 0);
  const hasRefund = po && amountPaid > 0 && po.payment_status !== "unpaid";

  useEffect(() => {
    if (open) {
      setNote("");
      setRefundAmount(String(activeValue.toFixed(2)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { note: note.trim() };
      if (hasRefund) {
        const amt = Number(refundAmount || 0);
        if (amt < 0) return toast.error("Refund amount cannot be negative");
        payload.refund_amount = amt;
      }
      const { data } = await api.post(`/purchase-orders/${po.id}/cancel`, payload);
      onDone?.(data);
      toast.success(`Canceled · ${activeLines.length} item(s) back to Requirements`);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!open || !po) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="cancel-po-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Cancel this entire PO?</DialogTitle>
          <DialogDescription>
            All {activeLines.length} remaining item(s) will return to Requirements. This PO will be locked as canceled.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md bg-[hsl(var(--muted))] p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span>Supplier</span>
              <span className="font-medium">{po.supplier_name || "—"}</span>
            </div>
            <div className="flex justify-between">
              <span>Items to re-raise</span>
              <span className="font-mono-data">{activeLines.length}</span>
            </div>
            <div className="flex justify-between">
              <span>Value</span>
              <span className="font-mono-data font-semibold">₹{activeValue.toFixed(2)}</span>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Reason / notes</Label>
            <Textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Supplier can't deliver this month"
              className="mt-1"
              data-testid="cancel-po-note"
            />
          </div>
          {hasRefund && (
            <div className="rounded-md border border-[#B4602A]/40 bg-amber-50 p-3 space-y-2">
              <div className="text-xs text-[#B4602A] font-medium">
                This PO was already paid (₹{amountPaid.toFixed(2)}). Record the refund amount expected from the supplier.
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Refund amount ₹</Label>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  className="mt-1 font-mono-data"
                  data-testid="cancel-po-refund-amount"
                />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Keep</Button>
          <Button
            onClick={submit}
            disabled={saving || activeLines.length === 0}
            className="bg-red-700 hover:bg-red-800 text-white"
            data-testid="cancel-po-confirm-btn"
          >
            {saving ? "Canceling…" : "Cancel entire PO"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

